import React, { useEffect, useState } from "react";
import { graphql } from "gatsby";
import {
  Box,
  Typography,
  Hidden,
  Button,
  useTheme,
  TextField,
  IconButton
} from "@material-ui/core";
import { NewsletterFragment as Props } from "./__generated__/NewsletterFragment";
import { formatMultilinesText } from "../../helpers/markdown";
import ChevronRight from "../../components/icons/chevron-right";
import { useForm } from "react-hook-form";
import useTranslate from "../../hooks/use-translate";
import useAuth, { useAuthContextSelector } from "../../hooks/use-auth";
import { subscribeToMailchimp } from "../../services/mailchimp";

export type NewsletterFormDataType = {
  email: string;
};

export default function NewsletterSection({
  title,
  emailInputLabel,
  cta,
  confirmationText,
  legalTextNode,
  mailchimpTags
}: Props) {
  const theme = useTheme();
  const t = useTranslate();
  const user = useAuthContextSelector(c => c.user);

  const [subscribed, setSubscribed] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    setError
  } = useForm<NewsletterFormDataType>();

  const handleFormSubmit = async (data: NewsletterFormDataType) => {
    try {
      await subscribeToMailchimp({
        email: data.email,
        tags: mailchimpTags?.split(",")
      });
      setSubscribed(true);
    } catch (e) {
      setError("email", { type: "error", message: e?.message });
    }
  };

  const showConfirmation = !!subscribed;

  // After current user is loaded, prefill textfield with email
  useEffect(() => {
    if (user) {
      setValue("email", user.email);
    }
  }, [user]);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{ display: "block" }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        paddingY={{ xs: 3, md: 6 }}
      >
        <Box
          display="flex"
          alignItems="stretch"
          justifyContent="space-between"
          flexDirection={{ xs: "column", md: "row" }}
          padding={{ xs: 2, md: 0 }}
          margin="0 auto"
          width="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            maxWidth="640px"
            flex={1}
          >
            <Box marginBottom={{ xs: 4, md: 2 }}>
              <Hidden xsDown>
                <Typography variant="h4">
                  {showConfirmation ? confirmationText : title}
                </Typography>
              </Hidden>
              <Hidden smUp>
                <Typography variant="h3">
                  {showConfirmation ? confirmationText : title}
                </Typography>
              </Hidden>
            </Box>

            <Box
              display="flex"
              alignItems={{ xs: "flex-start", md: "flex-end" }}
              marginBottom={{ xs: 6, md: 0 }}
              style={{
                visibility: showConfirmation ? "hidden" : "visible"
              }}
            >
              <Box
                width="100%"
                maxWidth={{ xs: "none", md: "325px" }}
                marginRight={{ xs: 0, md: 4 }}
              >
                <TextField
                  label={emailInputLabel}
                  fullWidth
                  name="email"
                  inputRef={register({
                    required: true,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: t("error.message.email")
                    }
                  })}
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email && errors.email.message}
                  disabled={formState.isSubmitting}
                  InputLabelProps={{ shrink: !!user ? true : undefined }}
                />
              </Box>

              <Hidden xsDown>
                <Button
                  variant="outlined"
                  style={{
                    minWidth: theme.spacing(24),
                    backgroundColor: theme.palette.common.white,
                    fontWeight: 600
                  }}
                  type="submit"
                >
                  {cta}
                </Button>
              </Hidden>
              <Hidden smUp>
                <Box marginTop={3}>
                  <IconButton type="submit">
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Hidden>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems=""
            justifyContent="flex-end"
            maxWidth={{ xs: "none", md: "300px" }}
            style={{
              visibility: showConfirmation ? "hidden" : "visible"
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              dangerouslySetInnerHTML={{
                __html: formatMultilinesText(
                  legalTextNode.childMarkdownRemark.html
                )
              }}
            />
          </Box>
        </Box>
      </Box>
    </form>
  );
}

export const NewsletterFragment = graphql`
  fragment NewsletterFragment on DatoCmsCModuleNewsletterSection {
    title
    emailInputLabel
    cta
    legalTextNode {
      childMarkdownRemark {
        html
      }
    }
    confirmationText
    mailchimpTags
    container
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    model {
      apiKey
    }
  }
`;
